/**
 * Daily Machine Prestart Prompt
 * Shown on the worker's Dashboard when they have an open machine usage / general hire
 * but no prestart submitted for today.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wrench, X } from "lucide-react";
import { Button, Card, Input, Textarea, Modal } from "../shared/ui";
import { useAuth } from "../App";
import { EnhancedPrestartModal } from "./EnhancedPrestartModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CHECKLIST = [
  "Engine oil level",
  "Coolant level",
  "Hydraulic fluid",
  "Tyre / track condition",
  "Lights and indicators",
  "Horns and reverse beepers",
  "Fire extinguisher",
  "Seat belt / restraints",
  "Visible damage / leaks",
];

export const PrestartPrompt = () => {
  const { currentCompany } = useAuth();
  const useEnhanced = currentCompany?.code === "MCS" || currentCompany?.settings?.enhanced_prestart === true;
  const [needed, setNeeded] = useState([]);
  const [activeMachine, setActiveMachine] = useState(null);
  const [hours, setHours] = useState("");
  const [kms, setKms] = useState("");
  const [notes, setNotes] = useState("");
  const [checklist, setChecklist] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/machine-prestarts/needed-for-user`);
      setNeeded(res.data?.prestarts_needed || []);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(id);
  }, []);

  const open = (m) => {
    setActiveMachine(m);
    setHours(m.current_hours || "");
    setKms(m.current_km || "");
    setNotes("");
    const init = {};
    DEFAULT_CHECKLIST.forEach((it) => (init[it] = true));
    setChecklist(init);
  };

  const submit = async () => {
    if (!activeMachine) return;
    if (!hours && hours !== 0) {
      toast.error("Hours required (current machine hour reading)");
      return;
    }
    setSubmitting(true);
    try {
      const cl = Object.entries(checklist).map(([item, ok]) => ({ item, ok }));
      const allOk = cl.every((c) => c.ok);
      await axios.post(`${API}/machine-prestarts`, {
        machine_id: activeMachine.machine_id,
        hours: parseFloat(hours),
        kilometres: kms ? parseFloat(kms) : null,
        notes: notes || null,
        checklist: cl,
        prestart_passed: allOk,
      });
      toast.success(`Prestart logged for ${activeMachine.name}`);
      setActiveMachine(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit prestart");
    } finally {
      setSubmitting(false);
    }
  };

  if (!needed.length) return null;

  return (
    <>
      <Card
        className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-white"
        data-testid="prestart-prompt-card"
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold uppercase text-purple-900 flex items-center gap-2">
            <Wrench className="w-5 h-5" /> Daily Machine Prestart Required
          </h3>
          <span className="text-xs text-purple-700">{needed.length} machine(s)</span>
        </div>
        <p className="text-xs text-purple-700 mb-3">
          You're operating these machines today — log a prestart with current hours/kms.
        </p>
        <div className="space-y-2">
          {needed.map((m) => (
            <div
              key={m.machine_id}
              className="bg-white rounded-lg p-2 flex items-center justify-between gap-2"
              data-testid={`prestart-needed-${m.machine_id}`}
            >
              <div>
                <p className="font-bold text-sm">{m.name}</p>
                <p className="text-xs text-slate-600">
                  {m.type?.replace("_", " ")} {m.rego ? `· ${m.rego}` : ""}
                  {m.current_hours != null ? ` · last ${m.current_hours}h` : ""}
                  {m.current_km != null ? ` · ${m.current_km}km` : ""}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => open(m)}
                data-testid={`prestart-open-${m.machine_id}`}
              >
                Do Prestart
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {activeMachine && useEnhanced && (
        <EnhancedPrestartModal
          machine={activeMachine}
          onClose={() => setActiveMachine(null)}
          onSubmitted={() => { setActiveMachine(null); refresh(); }}
        />
      )}

      {activeMachine && !useEnhanced && (
        <Modal
          isOpen
          onClose={() => setActiveMachine(null)}
          title={`Prestart — ${activeMachine.name}`}
          size="lg"
        >
          <div className="space-y-3" data-testid="prestart-form">
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Current Hours *"
                type="number"
                step="0.1"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                testId="prestart-hours"
              />
              <Input
                label="Current Kms"
                type="number"
                step="0.1"
                value={kms}
                onChange={(e) => setKms(e.target.value)}
                testId="prestart-kms"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-slate-600 mb-1 block">
                Checklist
              </label>
              <div className="space-y-1 max-h-64 overflow-y-auto border border-slate-200 rounded p-2">
                {DEFAULT_CHECKLIST.map((item) => (
                  <label
                    key={item}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm ${
                      checklist[item] === false ? "bg-red-50" : "hover:bg-slate-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checklist[item] !== false}
                      onChange={(e) =>
                        setChecklist((c) => ({ ...c, [item]: e.target.checked }))
                      }
                      data-testid={`prestart-check-${item.replace(/\s+/g, '-').toLowerCase()}`}
                    />
                    <span className={checklist[item] === false ? "line-through text-red-700" : ""}>
                      {item}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <Textarea
              label="Notes / Issues"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything to flag? Damage, faults, supplies needed..."
              rows={2}
              testId="prestart-notes"
            />
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setActiveMachine(null)}>
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="submit-prestart-btn"
              >
                {submitting ? "Saving..." : "Submit Prestart"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default PrestartPrompt;
